import React from "react";

// Global React error boundary. Mounted at the top of <App /> so any
// runtime exception thrown during rendering anywhere in the tree gets
// caught and replaced with a friendly recovery card — NOT a blank
// white screen. Play / App Store reviewers do test on flaky wifi and
// occasionally hit transient errors; we'd rather show them a polished
// "something went wrong" card than risk a "Stability" rejection.
//
// We keep the UI minimal so it works even if Tailwind/Sonner aren't
// fully booted yet (i.e. an error during initial render).
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Best-effort report so we can debug recurring crashes after
    // the user taps "Try again". Silently dropped if the endpoint
    // isn't reachable (e.g. offline reviewer).
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/client-errors`;
      navigator.sendBeacon?.(
        url,
        JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || ""),
          component_stack: String(info?.componentStack || ""),
          url: window.location.href,
          ua: navigator.userAgent,
        })
      );
    } catch (_) {}
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    // Hard refresh as a last-resort recovery — clears any wedged
    // state (stuck modals, dead service worker, etc.).
    try {
      window.location.reload();
    } catch (_) {}
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            backgroundColor: "#0e1116",
            color: "#f4ecd8",
            fontFamily:
              'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          }}
          data-testid="global-error-boundary"
        >
          <div style={{ maxWidth: 480, textAlign: "center" }}>
            <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 16 }}>✝</div>
            <h1 style={{ fontSize: 26, marginBottom: 8, fontWeight: 600 }}>
              Something went wrong
            </h1>
            <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 24, lineHeight: 1.55 }}>
              Holy Words hit an unexpected error. We've logged it. Try again,
              and if it keeps happening please email
              <br />
              <a
                href="mailto:support@holywords144business.com"
                style={{ color: "#e07a5f", textDecoration: "underline" }}
              >
                support@holywords144business.com
              </a>
              .
            </p>
            <button
              onClick={this.handleReset}
              data-testid="global-error-reset"
              style={{
                padding: "12px 28px",
                borderRadius: 999,
                border: 0,
                backgroundColor: "#e07a5f",
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
